import { cn } from "@/lib/utils";

interface Props {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

const H1: React.FC<Props> = ({ className, style, children }) => {
  return (
    <h1
      className={`scroll-m-20 text-4xl font-primary font-black lg:text-5xl ${className}`}
      style={style}
    >
      {children}
    </h1>
  );
};

const H2: React.FC<Props> = ({ className, children }) => {
  return (
    <h2 className={`scroll-m-20 text-3xl font-primary font-semibold first:mt-0 ${className}`}>
      {children}
    </h2>
  );
};

const H3: React.FC<Props> = ({ className, style, children }) => {
  return (
    <h3 className={cn("scroll-m-20 text-2xl font-primary font-semibold", className)} style={style}>
      {children}
    </h3>
  );
};

const H4: React.FC<Props> = ({ className, style, children }) => {
  return (
    <h4 className={cn("scroll-m-20 text-xl font-primary font-semibold", className)} style={style}>
      {children}
    </h4>
  );
};

const P = ({
  className,
  style,
  children,
  dangerouslySetInnerHTML,
}: {
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  dangerouslySetInnerHTML?: { __html: string };
}) => {
  return (
    <p
      className={`text-base lg:text-lg font-secondary !leading-[180%] [&:not(:first-child)]:mt-4 ${className}`}
      style={style}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </p>
  );
};

const Span = ({
  className,
  style,
  children,
}: {
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => {
  return (
    <span className={`text-base md:text-lg font-secondary ${className}`} style={style}>
      {children}
    </span>
  );
};

const Blockquote: React.FC<Props> = ({ className, children }) => {
  return (
    <blockquote className={cn("font-secondary mt-6 border-l-2 pl-6 italic", className)}>
      {children}
    </blockquote>
  );
};

const InlineCode: React.FC<Props> = ({ className, children }) => {
  return (
    <code
      className={cn(
        "relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold",
        className
      )}
    >
      {children}
    </code>
  );
};

const Lead: React.FC<Props> = ({ className, children }) => {
  return (
    <p className={cn(`font-secondary text-xl text-muted-foreground ${className}`)}>{children}</p>
  );
};

const Large: React.FC<Props> = ({ className, children }) => {
  return <div className={cn("font-secondary text-lg font-semibold", className)}>{children}</div>;
};

const Muted: React.FC<Props> = ({ className, children }) => {
  return (
    <p className={cn("font-secondary text-sm text-muted-foreground", className)}>{children}.</p>
  );
};

export { H1, H2, H3, H4, P, Lead, Large, Muted, Blockquote, InlineCode, Span };
